import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

const POLICY_TYPES = [
  { value: 'pii_detection',      label: 'PII Detection',      desc: 'Block SSNs, emails, credit cards' },
  { value: 'keyword_detection',  label: 'Keyword Detection',  desc: 'Block events containing specific words' },
  { value: 'tool_denylist',      label: 'Tool Denylist',      desc: 'Block specific tools' },
  { value: 'tool_allowlist',     label: 'Tool Allowlist',     desc: 'Only allow specific tools' },
  { value: 'budget_cap',         label: 'Budget Cap',         desc: 'Max cost per day' },
  { value: 'rate_limit',         label: 'Rate Limit',         desc: 'Max events per time window' },
  { value: 'token_limit',        label: 'Token Limit',        desc: 'Max tokens per trace' },
  { value: 'custom',             label: 'Custom Rule',        desc: 'Define custom policy logic' },
];

// Client-side PII patterns — instant feedback as user types
const PII_PATTERNS = [
  { name: 'Email address', re: /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g },
  { name: 'SSN',           re: /\b[0-9]{3}-[0-9]{2}-[0-9]{4}\b/g },
  { name: 'Credit card',   re: /\b[0-9]{4}[ -]?[0-9]{4}[ -]?[0-9]{4}[ -]?[0-9]{4}\b/g },
];

const KEYWORD_PATTERNS = [
  'password', 'passwd', 'api_key', 'apikey', 'secret_key',
  'private_key', 'access_token', 'auth_token', 'bearer', 'credentials',
];

const CATEGORY_ICONS = { Privacy: '🔒', Security: '🛡', Cost: '💰', Operations: '⚙️' };

const BUNDLE_ICONS = {
  'security-bundle': '🛡',
  'cost-control':    '💰',
  'read-only':       '📖',
  'compliance':      '⚖️',
};

// Preset test scenarios for the Policy Tester
const TEST_SCENARIOS = [
  {
    group: 'PII',
    samples: [
      { label: 'Email', text: 'Contact billing at support@company.com for invoice queries.' },
      { label: 'SSN', text: 'Patient record — John Doe, SSN 123-45-6789, DOB 1980-04-12.' },
      { label: 'Credit card', text: 'Payment processed: card 4111 1111 1111 1111, exp 12/26, CVV 123.' },
    ],
  },
  {
    group: 'Credentials',
    samples: [
      { label: 'API key', text: 'Using api_key=sk-abc123def456ghi to authenticate the request.' },
      { label: 'Password', text: 'Admin credentials: username=admin, password=S3cur3P@ss!' },
      { label: 'Bearer token', text: 'Authorization: bearer eyJhbGciOiJIUzI1NiJ9.payload.signature' },
    ],
  },
  {
    group: 'Clean',
    samples: [
      { label: 'Clean text', text: 'The weather today is sunny with a high of 72 degrees Fahrenheit.' },
      { label: 'Code review', text: 'Refactored the authentication module to use dependency injection.' },
    ],
  },
];

function Governance({ token }) {
  const toast = useToast();
  const [policies, setPolicies]       = useState([]);
  const [violations, setViolations]   = useState([]);
  const [templates, setTemplates]     = useState([]);
  const [bundles, setBundles]         = useState([]);
  const [agents, setAgents]           = useState([]);
  const [assignments, setAssignments] = useState({}); // agentID -> [policyID]
  const [tab, setTab]                 = useState('policies');
  const [showCreate, setShowCreate]   = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', policy_type: 'pii_detection', severity: 'warning', config: {},
  });

  // Assign modal
  const [assignModal, setAssignModal]   = useState(null);
  const [assignChecked, setAssignChecked] = useState({});

  // Policy Tester
  const [testInput, setTestInput]     = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testActionType, setTestActionType] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const pollRef = useRef(null);

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/policies`, { headers });
      if (res.ok) setPolicies((await res.json()).policies || []);
    } catch (e) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchViolations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/violations?limit=50`, { headers });
      if (res.ok) setViolations((await res.json()).violations || []);
    } catch (e) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/policies/templates`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        setBundles(data.bundles || []);
      }
    } catch (e) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/agents`, { headers });
      if (res.ok) setAgents((await res.json()).agents || []);
    } catch (e) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/agent-policy-assignments`, { headers });
      if (res.ok) setAssignments((await res.json()).assignments || {});
    } catch (e) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPolicies();
    fetchViolations();
    fetchTemplates();
    fetchAgents();
    fetchAssignments();
    pollRef.current = setInterval(fetchViolations, 5000);
    return () => clearInterval(pollRef.current);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPolicy = async () => {
    if (!form.name.trim()) { toast('Policy name is required', 'warning'); return; }
    try {
      const res = await fetch(`${API_URL}/api/protected/policies`, {
        method: 'POST', headers, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to create policy'));
      toast('Policy created', 'success');
      setShowCreate(false);
      setForm({ name: '', description: '', policy_type: 'pii_detection', severity: 'warning', config: {} });
      fetchPolicies();
    } catch (e) { toast(e.message, 'error'); }
  };

  const installTemplate = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/policies/templates/${slug}/install`, {
        method: 'POST', headers, body: '{}',
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to install template'));
      toast('Policy installed!', 'success');
      fetchPolicies();
      setTab('policies');
    } catch (e) { toast(e.message, 'error'); }
  };

  const installBundle = async (slug, bundleName) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/policies/bundles/${slug}/install`, {
        method: 'POST', headers, body: '{}',
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to install bundle'));
      const data = await res.json();
      toast(
        `${bundleName} installed — ${data.installed} new, ${data.skipped} already active`,
        'success',
      );
      fetchPolicies();
      setTab('policies');
    } catch (e) { toast(e.message, 'error'); }
  };

  const togglePolicy = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/policies/${id}/toggle`, { method: 'PATCH', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to toggle'));
      fetchPolicies();
    } catch (e) { toast(e.message, 'error'); }
  };

  const deletePolicy = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/policies/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to delete'));
      toast('Policy deleted', 'success');
      fetchPolicies();
      fetchAssignments();
    } catch (e) { toast(e.message, 'error'); }
  };

  const openAssignModal = (policy) => {
    setAssignModal(policy);
    const checked = {};
    Object.entries(assignments).forEach(([agentId, policyIds]) => {
      if (policyIds.includes(policy.id)) checked[agentId] = true;
    });
    setAssignChecked(checked);
  };

  const saveAssignments = async () => {
    if (!assignModal) return;
    const policyId = assignModal.id;
    const toAdd = [];
    const toRemove = [];
    agents.forEach(agent => {
      const wasAssigned = Object.entries(assignments).some(
        ([aid, pids]) => Number(aid) === agent.id && pids.includes(policyId)
      );
      const isNowChecked = !!assignChecked[agent.id];
      if (isNowChecked && !wasAssigned) toAdd.push(agent.id);
      if (!isNowChecked && wasAssigned) toRemove.push(agent.id);
    });
    try {
      await Promise.all([
        ...toAdd.map(agentId =>
          fetch(`${API_URL}/api/protected/agents/${agentId}/policies`, {
            method: 'POST', headers, body: JSON.stringify({ policy_id: policyId }),
          })
        ),
        ...toRemove.map(agentId =>
          fetch(`${API_URL}/api/protected/agents/${agentId}/policies/${policyId}`, {
            method: 'DELETE', headers,
          })
        ),
      ]);
      toast('Assignments saved', 'success');
      setAssignModal(null);
      fetchAssignments();
    } catch (e) { toast('Failed to save assignments', 'error'); }
  };

  const runPolicyTest = async () => {
    if (!testInput.trim()) { toast('Enter some text to test', 'warning'); return; }
    setTestLoading(true);
    setTestResults(null);
    try {
      const res = await fetch(`${API_URL}/api/protected/policies/check`, {
        method: 'POST', headers,
        body: JSON.stringify({ text: testInput, action_type: testActionType || 'manual_test' }),
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Test failed'));
      const data = await res.json();
      setTestResults(data);
      if (data.total > 0) fetchViolations();
    } catch (e) { toast(e.message, 'error'); }
    finally { setTestLoading(false); }
  };

  const getAssignedAgentNames = (policyId) => {
    const names = [];
    Object.entries(assignments).forEach(([aid, pids]) => {
      if (pids.includes(policyId)) {
        const agent = agents.find(a => a.id === Number(aid));
        if (agent) names.push(agent.name);
      }
    });
    return names;
  };

  // Bundle coverage: count how many of its templates are already installed
  const bundleCoverage = (bundle) => {
    const installed = bundle.templates.filter(slug =>
      templates.some(t => t.slug === slug && policies.some(p => p.name === t.name))
    );
    return { installed: installed.length, total: bundle.templates.length };
  };

  const clientPiiHits = React.useMemo(() => {
    if (!testInput.trim()) return [];
    return PII_PATTERNS.flatMap(p => {
      const matches = testInput.match(p.re);
      return matches ? [{ ...p, matches }] : [];
    });
  }, [testInput]);

  const clientKeywordHits = React.useMemo(() => {
    if (!testInput.trim()) return [];
    const lower = testInput.toLowerCase();
    return KEYWORD_PATTERNS.filter(k => lower.includes(k));
  }, [testInput]);

  const piiPolicyEnabled      = policies.some(p => p.enabled && p.policy_type === 'pii_detection');
  const keywordPolicyEnabled  = policies.some(p => p.enabled && p.policy_type === 'keyword_detection');

  const newViolationsCount = violations.filter(v =>
    Date.now() - new Date(v.created_at).getTime() < 60000
  ).length;

  const severityBadge = (severity) => {
    const map = { info: 'badge-info', warning: 'badge-warning', critical: 'badge-error' };
    return <span className={`badge ${map[severity] || 'badge-neutral'}`}>{severity}</span>;
  };

  const policyTypeLabel = (type) => {
    const found = POLICY_TYPES.find(p => p.value === type);
    return found ? found.label : type;
  };

  const getConfigFields = () => {
    switch (form.policy_type) {
      case 'budget_cap':
        return (
          <div className="form-group">
            <label>Max Cost ($/day)</label>
            <input type="number" step="0.01" value={form.config.max_cost || ''}
              onChange={e => setForm({ ...form, config: { max_cost: parseFloat(e.target.value) } })} />
          </div>
        );
      case 'rate_limit':
        return (
          <div className="form-row">
            <div className="form-group">
              <label>Max Events</label>
              <input type="number" value={form.config.max_requests || ''}
                onChange={e => setForm({ ...form, config: { ...form.config, max_requests: parseInt(e.target.value) } })} />
            </div>
            <div className="form-group">
              <label>Window</label>
              <select value={form.config.window || '1 hour'}
                onChange={e => setForm({ ...form, config: { ...form.config, window: e.target.value } })}>
                <option value="1 hour">1 hour</option>
                <option value="2 hours">2 hours</option>
                <option value="4 hours">4 hours</option>
                <option value="12 hours">12 hours</option>
                <option value="1 day">1 day</option>
                <option value="7 days">7 days</option>
              </select>
            </div>
          </div>
        );
      case 'tool_allowlist':
      case 'tool_denylist':
        return (
          <div className="form-group">
            <label>Tools (comma-separated)</label>
            <input value={(form.config.tools || []).join(', ')}
              onChange={e => setForm({ ...form, config: { tools: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })}
              placeholder="bash, write, web_search" />
            <div className="text-xs text-muted" style={{ marginTop: 4 }}>
              Matching is case-insensitive. Claude Code tool names: bash, write, edit, webfetch, websearch, read, glob, task
            </div>
          </div>
        );
      case 'token_limit':
        return (
          <div className="form-group">
            <label>Max Tokens Per Trace</label>
            <input type="number" value={form.config.max_tokens || ''}
              onChange={e => setForm({ ...form, config: { max_tokens: parseInt(e.target.value) } })} />
          </div>
        );
      case 'pii_detection':
        return (
          <div className="form-group">
            <p className="text-xs text-muted">
              Automatically detects emails, SSNs, and credit card numbers. No additional config needed.
            </p>
          </div>
        );
      case 'keyword_detection':
        return (
          <div className="form-group">
            <label>Blocked Keywords (comma-separated)</label>
            <input value={(form.config.keywords || []).join(', ')}
              onChange={e => setForm({ ...form, config: { keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })}
              placeholder="password, api_key, secret, bearer" />
            <div className="text-xs text-muted" style={{ marginTop: 4 }}>
              Matching is case-insensitive and searches all event fields (action, target, metadata content).
            </div>
          </div>
        );
      default:
        return (
          <div className="form-group">
            <label>Config (JSON)</label>
            <textarea value={JSON.stringify(form.config, null, 2)}
              onChange={e => { try { setForm({ ...form, config: JSON.parse(e.target.value) }); } catch (err) {} }}
              rows={4} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </div>
        );
    }
  };

  const templatesByCategory = templates.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const categoryOrder = ['Privacy', 'Security', 'Cost', 'Operations'];
  const sortedCategories = [
    ...categoryOrder.filter(c => templatesByCategory[c]),
    ...Object.keys(templatesByCategory).filter(c => !categoryOrder.includes(c)),
  ];

  return (
    <>
      {/* Assign modal */}
      {assignModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="card" style={{ width: 420, maxHeight: '80vh', overflow: 'auto', margin: 0 }}>
            <div className="card-title" style={{ marginBottom: 4 }}>Assign Policy to Agents</div>
            <div className="text-xs text-muted" style={{ marginBottom: 16 }}>
              <strong>{assignModal.name}</strong> — select which agents this policy applies to.
              Agents with no specific policies use all your global policies.
            </div>
            {agents.length === 0 ? (
              <p className="text-sm text-muted">No agents registered yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {agents.map(agent => (
                  <label key={agent.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={!!assignChecked[agent.id]}
                      onChange={e => setAssignChecked(prev => ({ ...prev, [agent.id]: e.target.checked }))} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{agent.name}</div>
                      <div className="text-xs text-muted">{agent.framework} · {agent.status}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={saveAssignments}>Save</button>
              <button className="btn-secondary" onClick={() => setAssignModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Governance</div>
          <div className="page-subtitle">Policies, guardrails, and compliance</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => { setTab('templates'); fetchTemplates(); }}>
            Browse Templates
          </button>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>New Policy</button>
        </div>
      </div>

      <div className="page-body">
        <div className="tabs">
          <button className={`tab-btn ${tab === 'policies' ? 'active' : ''}`} onClick={() => setTab('policies')}>
            Policies ({policies.length})
          </button>
          <button className={`tab-btn ${tab === 'violations' ? 'active' : ''}`}
            onClick={() => { setTab('violations'); fetchViolations(); }}>
            Violations ({violations.length})
            {newViolationsCount > 0 && (
              <span className="badge badge-error" style={{ marginLeft: 6 }}>+{newViolationsCount} new</span>
            )}
          </button>
          <button className={`tab-btn ${tab === 'tester' ? 'active' : ''}`} onClick={() => setTab('tester')}>
            Policy Tester
          </button>
          <button className={`tab-btn ${tab === 'templates' ? 'active' : ''}`}
            onClick={() => { setTab('templates'); fetchTemplates(); }}>
            Templates ({templates.length})
          </button>
        </div>

        {showCreate && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Create Policy</div>
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. No Credentials in Output" />
              </div>
              <div className="form-group">
                <label>Policy Type</label>
                <select value={form.policy_type} onChange={e => setForm({ ...form, policy_type: e.target.value, config: {} })}>
                  {POLICY_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label} — {pt.desc}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Severity</label>
                <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe what this policy does" />
              </div>
            </div>
            {getConfigFields()}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={createPolicy}>Create Policy</button>
              <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Policies tab ── */}
        {tab === 'policies' && (
          <div className="card">
            {policies.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🛡</div>
                <h3>No policies yet</h3>
                <p>Install a bundle for instant coverage, or browse individual templates.</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button className="btn-primary" onClick={() => { setTab('templates'); fetchTemplates(); }}>
                    Browse Templates
                  </button>
                  <button className="btn-secondary" onClick={() => setShowCreate(true)}>Create Custom</button>
                </div>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Policy</th>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Violations</th>
                    <th>Assigned To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map(p => {
                    const assignedNames = getAssignedAgentNames(p.id);
                    return (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{p.name}</div>
                          <div className="text-xs text-muted">{p.description}</div>
                        </td>
                        <td><span className="badge badge-info">{policyTypeLabel(p.policy_type)}</span></td>
                        <td>{severityBadge(p.severity)}</td>
                        <td>
                          <button className="btn-ghost btn-sm" onClick={() => togglePolicy(p.id)}
                            style={{ color: p.enabled ? 'var(--success)' : 'var(--text-muted)' }}>
                            <span className={`status-dot ${p.enabled ? 'active' : 'inactive'}`} />
                            {p.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </td>
                        <td>
                          {p.violations_count > 0 ? (
                            <button className="badge badge-error"
                              style={{ cursor: 'pointer', border: 'none', background: 'none' }}
                              onClick={() => setTab('violations')}>
                              {p.violations_count}
                            </button>
                          ) : <span className="text-muted">0</span>}
                        </td>
                        <td>
                          {assignedNames.length > 0 ? (
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                              {assignedNames.join(', ')}
                            </span>
                          ) : (
                            <span className="text-xs text-muted">All agents</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn-secondary btn-sm" onClick={() => openAssignModal(p)}>Assign</button>
                            <button className="btn-danger btn-sm" onClick={() => deletePolicy(p.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Violations tab ── */}
        {tab === 'violations' && (
          <div className="card">
            {violations.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">✓</div>
                <h3>No violations</h3>
                <p>No policy violations detected. Use the Policy Tester to generate one instantly.</p>
                <button className="btn-secondary" onClick={() => setTab('tester')}>Open Policy Tester</button>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Policy</th>
                    <th>Description</th>
                    <th>Agent</th>
                    <th>Severity</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.map(v => (
                    <tr key={v.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{v.policy_name}</td>
                      <td>{v.description}</td>
                      <td>{v.agent_name || <span className="text-muted">-</span>}</td>
                      <td>{severityBadge(v.severity)}</td>
                      <td className="text-sm">{new Date(v.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Policy Tester tab ── */}
        {tab === 'tester' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-title" style={{ marginBottom: 4 }}>Policy Tester</div>
              <div className="text-sm text-muted" style={{ marginBottom: 16 }}>
                Paste any text to test against your active policies. PII and keyword violations highlight as you type.
              </div>

              {/* Quick test scenario buttons */}
              <div style={{ marginBottom: 12 }}>
                {TEST_SCENARIOS.map(group => (
                  <div key={group.group} style={{ marginBottom: 8 }}>
                    <div className="text-xs text-muted" style={{ marginBottom: 4 }}>{group.group}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {group.samples.map(s => (
                        <button key={s.label} className="btn-secondary btn-sm"
                          onClick={() => { setTestInput(s.text); setTestResults(null); }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-row" style={{ alignItems: 'flex-end', marginBottom: 8 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Text / Content</label>
                  <textarea value={testInput}
                    onChange={e => { setTestInput(e.target.value); setTestResults(null); }}
                    rows={5} placeholder="Paste agent output, message content, or any text to test..."
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
                </div>
                <div className="form-group" style={{ width: 200 }}>
                  <label>Action Type (optional)</label>
                  <input value={testActionType}
                    onChange={e => setTestActionType(e.target.value)}
                    placeholder="e.g. bash, write, user_message" />
                  <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                    Tool denylist/allowlist checks match against this
                  </div>
                </div>
              </div>

              {/* Real-time PII indicator */}
              {testInput.trim() && (
                <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {clientPiiHits.length > 0 ? (
                    <div className="alert error">
                      <strong>PII detected:</strong>
                      {clientPiiHits.map((h, i) => (
                        <span key={i} style={{ marginLeft: 8 }}>
                          <span className="badge badge-error">{h.name}</span>
                          <span className="text-xs text-muted" style={{ marginLeft: 4 }}>
                            ({h.matches.slice(0, 2).join(', ')}{h.matches.length > 2 ? '...' : ''})
                          </span>
                        </span>
                      ))}
                      {!piiPolicyEnabled && (
                        <span className="text-xs text-muted" style={{ marginLeft: 12 }}>
                          — No PII policy enabled.{' '}
                          <button className="btn-ghost btn-sm" onClick={() => { setTab('templates'); fetchTemplates(); }}>
                            Install one
                          </button>
                        </span>
                      )}
                    </div>
                  ) : null}

                  {clientKeywordHits.length > 0 ? (
                    <div className="alert error">
                      <strong>Credential keywords found:</strong>{' '}
                      {clientKeywordHits.map(k => (
                        <span key={k} className="badge badge-error" style={{ marginLeft: 4 }}>{k}</span>
                      ))}
                      {!keywordPolicyEnabled && (
                        <span className="text-xs text-muted" style={{ marginLeft: 12 }}>
                          — No Keyword Detection policy enabled.{' '}
                          <button className="btn-ghost btn-sm" onClick={() => { setTab('templates'); fetchTemplates(); }}>
                            Install one
                          </button>
                        </span>
                      )}
                    </div>
                  ) : null}

                  {clientPiiHits.length === 0 && clientKeywordHits.length === 0 && (
                    <div className="alert success" style={{ padding: '6px 12px' }}>
                      <span className="text-sm">No PII or credential keywords detected in text.</span>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn-primary" onClick={runPolicyTest} disabled={testLoading || !testInput.trim()}>
                  {testLoading ? 'Checking…' : 'Check All Policies'}
                </button>
                <span className="text-xs text-muted">
                  Checks all {policies.filter(p => p.enabled).length} active policies
                </span>
                {testResults !== null && (
                  <button className="btn-ghost btn-sm" onClick={() => { setTestInput(''); setTestResults(null); }}>
                    Clear
                  </button>
                )}
              </div>
            </div>

            {testResults !== null && (
              <div className="card">
                {testResults.total === 0 ? (
                  <div className="alert success">
                    <strong>All policies passed.</strong>{' '}
                    {policies.filter(p => p.enabled).length} policies checked — no violations.
                  </div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12, color: 'var(--error)' }}>
                      {testResults.total} violation{testResults.total > 1 ? 's' : ''} found:
                    </div>
                    <table>
                      <thead>
                        <tr><th>Policy</th><th>Type</th><th>Description</th><th>Severity</th></tr>
                      </thead>
                      <tbody>
                        {testResults.violations.map((v, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{v.policy_name}</td>
                            <td><span className="badge badge-info">{policyTypeLabel(v.policy_type)}</span></td>
                            <td>{v.description}</td>
                            <td>{severityBadge(v.severity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Templates tab ── */}
        {tab === 'templates' && (
          <div>
            {/* Bundles section */}
            {bundles.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Quick Start Bundles</div>
                <div className="text-xs text-muted" style={{ marginBottom: 16 }}>
                  Install a curated set of policies with one click. Already-active policies are skipped automatically.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {bundles.map(b => {
                    const cov = bundleCoverage(b);
                    const fullyInstalled = cov.installed === cov.total;
                    return (
                      <div key={b.slug} className="card" style={{
                        margin: 0, padding: 20,
                        border: fullyInstalled ? '1px solid var(--success)' : '1px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>
                            {BUNDLE_ICONS[b.slug] || '📦'} {b.name}
                          </div>
                          <span className="badge badge-info">{b.category}</span>
                        </div>
                        <div className="text-xs text-muted" style={{ marginBottom: 12, lineHeight: 1.5 }}>
                          {b.description}
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <div className="text-xs text-muted" style={{ marginBottom: 4 }}>Includes:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {b.templates.map(slug => {
                              const tmpl = templates.find(t => t.slug === slug);
                              const active = tmpl && policies.some(p => p.name === tmpl.name);
                              return (
                                <span key={slug} className={`badge ${active ? 'badge-success' : 'badge-neutral'}`}
                                  style={{ fontSize: 10 }}>
                                  {tmpl ? tmpl.name : slug}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {fullyInstalled ? (
                            <span className="badge badge-success">All {cov.total} installed</span>
                          ) : (
                            <button className="btn-primary btn-sm" onClick={() => installBundle(b.slug, b.name)}>
                              Install Bundle ({cov.total - cov.installed} new)
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Individual templates by category */}
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Individual Templates</div>
            {sortedCategories.length === 0 ? (
              <div className="card"><p className="text-muted">Loading templates…</p></div>
            ) : (
              sortedCategories.map(category => (
                <div key={category} style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: 'var(--text-secondary)' }}>
                    {CATEGORY_ICONS[category] || '•'} {category}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                    {templatesByCategory[category].map(t => {
                      const alreadyInstalled = policies.some(p => p.name === t.name);
                      return (
                        <div key={t.slug} className="card" style={{ margin: 0, padding: 14 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{t.name}</div>
                            {severityBadge(t.severity)}
                          </div>
                          <div className="text-xs text-muted" style={{ marginBottom: 10, lineHeight: 1.5 }}>
                            {t.description}
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {alreadyInstalled ? (
                              <span className="badge badge-success">Installed</span>
                            ) : (
                              <button className="btn-primary btn-sm" onClick={() => installTemplate(t.slug)}>
                                + Install
                              </button>
                            )}
                            <span className="badge badge-info" style={{ fontSize: 10 }}>
                              {policyTypeLabel(t.policy_type)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default Governance;
